/*
In total we have 5 environments: local, dev, staging, prod and preview (used for PRs).
Local and dev are the same. Each of dev/staging/prod has a corresponding preview env (for PRs).
So we consider only 3: dev/staging/prod.

On any environment frontend should be able to select Network.
For every environment there are predefined default values per Network.
These defaults can be overriden via env vars with Network suffixes.
*/

import type { ConfigNetworkSettings, EnvName } from './config.types';
import { getDomainEnv } from './config.utils';

// Partial<ChainNetwork>
export type ConfigNetworkName = 'regtest' | 'mutiny' | 'testnet4' | 'main';

// Regtest is for usage with locally running ducat-infra:
const regtestConfig: ConfigNetworkSettings = {
  network: 'regtest',
  guardianUrlTemplate: 'ws://localhost:8081',
  validatorUrl: 'http://localhost:8083',
  oraclePubkey: '3752ade673753a7be34f4b24486151e66e7df781bde6d0c4cababd94aaa45a7a',
  oracleRelayWS: 'ws://localhost:8082',
  esploraUrl: 'http://localhost:3002',
  mempoolUrl: 'http://localhost:3002',
};

const mutinyConfig: ConfigNetworkSettings = {
  network: 'mutiny',
  guardianUrlTemplate: 'wss://guardian-{num}-mutinynet.dev.ducatprotocol.com/ws',
  validatorUrl: 'https://validator-mutinynet.dev.ducatprotocol.com',
  oraclePubkey: 'a12736a47c9e8f20c863bff8c35fa7db2d79875e5812f419799da2e8ec7cd41e',
  oracleRelayWS: 'wss://relay-mutinynet.dev.ducatprotocol.com',
  esploraUrl: 'https://mutinynet.com/api',
  mempoolUrl: 'https://explorer-mutinynet.dev.ducatprotocol.com', // 'https://mutinynet.com'
};

const testnet4Config: ConfigNetworkSettings = {
  network: 'testnet4',
  guardianUrlTemplate: 'wss://guardian-{num}-testnet4.dev.ducatprotocol.com/ws',
  validatorUrl: 'https://validator-testnet4.dev.ducatprotocol.com',
  oraclePubkey: 'a12736a47c9e8f20c863bff8c35fa7db2d79875e5812f419799da2e8ec7cd41e',
  oracleRelayWS: 'wss://relay-testnet4.dev.ducatprotocol.com',
  esploraUrl: 'https://mempool.space/testnet4/api',
  mempoolUrl: 'https://explorer-testnet4.dev.ducatprotocol.com',
};

const mainnetConfig: ConfigNetworkSettings = {
  network: 'main',
  guardianUrlTemplate: 'wss://guardian-{num}.ducatprotocol.com/ws',
  validatorUrl: 'https://validator.ducatprotocol.com',
  oraclePubkey: 'a12736a47c9e8f20c863bff8c35fa7db2d79875e5812f419799da2e8ec7cd41e',
  oracleRelayWS: 'wss://relay.ducatprotocol.com',
  esploraUrl: 'https://mempool.space/api',
  mempoolUrl: 'https://explorer.ducatprotocol.com',
};

const configMatrix: Record<EnvName, Record<ConfigNetworkName, ConfigNetworkSettings>> = {
  dev: {
    regtest: regtestConfig,
    mutiny: mutinyConfig,
    testnet4: testnet4Config,
    main: mainnetConfig,
  },
  staging: {
    regtest: regtestConfig,
    mutiny: {
      ...mutinyConfig,
      // Share prod's reservation and pending-input locks for the same on-chain accounts.
      guardianUrlTemplate: 'wss://guardian-{num}-mutinynet.prod.ducatprotocol.com/ws',
      validatorUrl: 'https://validator-mutinynet.staging.ducatprotocol.com',
      oracleRelayWS: 'wss://relay-mutinynet.staging.ducatprotocol.com',
      mempoolUrl: 'https://explorer-mutinynet.staging.ducatprotocol.com',
    },
    testnet4: testnet4Config,
    main: mainnetConfig,
  },
  prod: {
    regtest: regtestConfig,
    mutiny: {
      ...mutinyConfig,
      guardianUrlTemplate: 'wss://guardian-{num}-mutinynet.prod.ducatprotocol.com/ws',
      validatorUrl: 'https://validator-mutinynet.prod.ducatprotocol.com',
      oracleRelayWS: 'wss://relay-mutinynet.prod.ducatprotocol.com',
      mempoolUrl: 'https://explorer-mutinynet.prod.ducatprotocol.com',
    },
    testnet4: testnet4Config,
    main: mainnetConfig,
  },
};

function getDefault(propName: keyof ConfigNetworkSettings, network: ConfigNetworkName, env: EnvName) {
  return configMatrix[env][network][propName];
}

const envName: EnvName = (process.env.NEXT_PUBLIC_ENV as EnvName) || getDomainEnv(); // based on domain name.

export const configsByNetwork: Record<ConfigNetworkName, ConfigNetworkSettings & { envName: EnvName }> = {
  regtest: {
    network: 'regtest',
    guardianUrlTemplate: process.env.NEXT_PUBLIC_GUARDIAN_URL_TEMPLATE_REGTEST || getDefault('guardianUrlTemplate', 'regtest', envName),
    validatorUrl: process.env.NEXT_PUBLIC_VALIDATOR_URL_REGTEST || getDefault('validatorUrl', 'regtest', envName),
    oraclePubkey: process.env.NEXT_PUBLIC_ORACLE_PUBKEY_REGTEST || getDefault('oraclePubkey', 'regtest', envName),
    oracleRelayWS: process.env.NEXT_PUBLIC_ORACLE_RELAY_WS_REGTEST || getDefault('oracleRelayWS', 'regtest', envName),
    esploraUrl: process.env.NEXT_PUBLIC_ESPLORA_URL_REGTEST || getDefault('esploraUrl', 'regtest', envName),
    mempoolUrl: process.env.NEXT_PUBLIC_MEMPOOL_URL_REGTEST || getDefault('mempoolUrl', 'regtest', envName),
    envName,
  },
  mutiny: {
    network: 'mutiny',
    guardianUrlTemplate: process.env.NEXT_PUBLIC_GUARDIAN_URL_TEMPLATE_MUTINY || getDefault('guardianUrlTemplate', 'mutiny', envName),
    validatorUrl: process.env.NEXT_PUBLIC_VALIDATOR_URL_MUTINY || getDefault('validatorUrl', 'mutiny', envName),
    oraclePubkey: process.env.NEXT_PUBLIC_ORACLE_PUBKEY_MUTINY || getDefault('oraclePubkey', 'mutiny', envName),
    oracleRelayWS: process.env.NEXT_PUBLIC_ORACLE_RELAY_WS_MUTINY || getDefault('oracleRelayWS', 'mutiny', envName),
    esploraUrl: process.env.NEXT_PUBLIC_ESPLORA_URL_MUTINY || getDefault('esploraUrl', 'mutiny', envName),
    mempoolUrl: process.env.NEXT_PUBLIC_MEMPOOL_URL_MUTINY || getDefault('mempoolUrl', 'mutiny', envName),
    envName,
  },
  testnet4: {
    network: 'testnet4',
    guardianUrlTemplate: process.env.NEXT_PUBLIC_GUARDIAN_URL_TEMPLATE_TESTNET4 || getDefault('guardianUrlTemplate', 'testnet4', envName),
    validatorUrl: process.env.NEXT_PUBLIC_VALIDATOR_URL_TESTNET4 || getDefault('validatorUrl', 'testnet4', envName),
    oraclePubkey: process.env.NEXT_PUBLIC_ORACLE_PUBKEY_TESTNET4 || getDefault('oraclePubkey', 'testnet4', envName),
    oracleRelayWS: process.env.NEXT_PUBLIC_ORACLE_RELAY_WS_TESTNET4 || getDefault('oracleRelayWS', 'testnet4', envName),
    esploraUrl: process.env.NEXT_PUBLIC_ESPLORA_URL_TESTNET4 || getDefault('esploraUrl', 'testnet4', envName),
    mempoolUrl: process.env.NEXT_PUBLIC_MEMPOOL_URL_TESTNET4 || getDefault('mempoolUrl', 'testnet4', envName),
    envName,
  },
  main: {
    network: 'main',
    guardianUrlTemplate: process.env.NEXT_PUBLIC_GUARDIAN_URL_TEMPLATE_MAINNET || getDefault('guardianUrlTemplate', 'main', envName),
    validatorUrl: process.env.NEXT_PUBLIC_VALIDATOR_URL_MAINNET || getDefault('validatorUrl', 'main', envName),
    oraclePubkey: process.env.NEXT_PUBLIC_ORACLE_PUBKEY_MAINNET || getDefault('oraclePubkey', 'main', envName),
    oracleRelayWS: process.env.NEXT_PUBLIC_ORACLE_RELAY_WS_MAINNET || getDefault('oracleRelayWS', 'main', envName),
    esploraUrl: process.env.NEXT_PUBLIC_ESPLORA_URL_MAINNET || getDefault('esploraUrl', 'main', envName),
    mempoolUrl: process.env.NEXT_PUBLIC_MEMPOOL_URL_MAINNET || getDefault('mempoolUrl', 'main', envName),
    envName,
  },
};

/*
Guardian: GUARDIAN_URL_TEMPLATE
- dev: 
  - Regtest:  ws://localhost:8081
  - Mutiny:   wss://guardian-mutinynet-{num}.dev.ducatprotocol.com/ws
  - Mainnet:  wss://guardian-mainnet-{num}.dev.ducatprotocol.com/ws
- staging:
  - Regtest:  ws://localhost:8081
  - Mutiny:   wss://guardian-mutinynet-{num}.staging.ducatprotocol.com/ws
  - Mainnet:  wss://guardian-mainnet-{num}.staging.ducatprotocol.com/ws
- prod:
  - Regtest:  ws://localhost:8081
  - Mutiny:   wss://guardian-mutinynet-{num}.prod.ducatprotocol.com/ws
  - Mainnet:  wss://guardian-mainnet-{num}.prod.ducatprotocol.com/ws

Validator: VALIDATOR_URL
- dev: 
  - Regtest:  http://localhost:8083
  - Mutiny:   https://validator-mutinynet.dev.ducatprotocol.com
  - Mainnet:  https://validator-mainnet.dev.ducatprotocol.com
- staging: 
  - Regtest:  http://localhost:8083
  - Mutiny:   https://validator-mutinynet.staging.ducatprotocol.com
  - Mainnet:  https://validator-mainnet.staging.ducatprotocol.com
- prod: 
  - Regtest:  http://localhost:8083
  - Mutiny:   https://validator-mutinynet.prod.ducatprotocol.com
  - Mainnet:  https://validator-mainnet.prod.ducatprotocol.com

Oracle: ORACLE_PUBKEY, ORACLE_RELAY_WS
- dev:
  - Regtest:  ORACLE_PUBKEY=3752ade673753a7be34f4b24486151e66e7df781bde6d0c4cababd94aaa45a7a
              ORACLE_RELAY_WS=ws://localhost:8082

  - Mutiny:   ORACLE_PUBKEY=a12736a47c9e8f20c863bff8c35fa7db2d79875e5812f419799da2e8ec7cd41e
              ORACLE_RELAY_WS=wss://dev-relay.ducatprotocol.com
              
  - Mainnet:  ORACLE_PUBKEY=...
              ORACLE_RELAY_WS=wss://prod-relay.ducatprotocol.com

Esplora:
Mempool:
*/
